'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface HeroSectionProps {
  isHydrated: boolean;
}

const HeroSection = ({ isHydrated }: HeroSectionProps) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (!isHydrated) return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHydrated]);

  const parallaxOffset = isHydrated ? scrollY * 0.5 : 0;

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-background overflow-hidden">
      {/* Parallax Background Element */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-muted/20 to-transparent pointer-events-none"
        style={{
          transform: isHydrated ? `translateY(${parallaxOffset}px)` : 'translateY(0)',
          transition: 'transform 0.1s ease-out'
        }} />


      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="text-center space-y-6 lg:space-y-8">
          {/* Animated Main Heading */}
          <h1
            className="font-headline font-bold text-primary leading-[0.9] tracking-tighter"
            style={{
              fontSize: 'clamp(3rem, 12vw, 10rem)',
              opacity: isHydrated ? 1 : 0,
              transform: isHydrated ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
            }}>

            VALINAIRE
          </h1>

          {/* Tagline */}
          <p
            className="font-headline text-2xl lg:text-4xl text-primary tracking-wide"
            style={{
              opacity: isHydrated ? 1 : 0,
              transform: isHydrated ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out 0.1s, transform 0.8s ease-out 0.1s'
            }}>

            Your Digital Growth Partner
          </p>

          {/* Brand Statement */}
          <p
            className="font-body text-text-secondary text-lg lg:text-2xl max-w-3xl mx-auto leading-relaxed tracking-wide"
            style={{
              opacity: isHydrated ? 1 : 0,
              transform: isHydrated ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
            }}>Helping the Triangle area businesses and online brands grow through social media management, paid digital marketing, business automation, and custom website development.


          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 lg:gap-5"
            style={{
              opacity: isHydrated ? 1 : 0,
              transform: isHydrated ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s'
            }}>

            <Link
              href="/contact"
              className="group relative px-8 lg:px-12 py-4 lg:py-5 bg-primary text-primary-foreground font-cta font-semibold text-sm lg:text-base tracking-wide border border-primary overflow-hidden transition-all duration-fast hover:-translate-y-1">

              <span className="relative z-10">Let&apos;s Grow Your Business</span>
              <div className="absolute inset-0 bg-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-medium origin-left" />
            </Link>

            <Link
              href="/services"
              className="group px-8 lg:px-12 py-4 lg:py-5 bg-transparent text-primary font-cta font-semibold text-sm lg:text-base tracking-wide border border-primary transition-all duration-fast hover:-translate-y-1 hover:bg-primary hover:text-primary-foreground">

              Explore Services
            </Link>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;